import { apiFetch } from "../../../../services/api";
// API service for chat dashboard overview data
const API_BASE_URL = (process.env.REACT_APP_CHAT_API_BASE_URL ?? '').replace(/\/+$/, '');

export interface TopicsDistributionQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface TopicDistributionItem {
  id: number;
  name: string;
  sdg_number: number;
  message_count: number;
}

export interface TopicsDistributionResponse {
  success: boolean;
  data: {
    topics: TopicDistributionItem[];
    others?: {
      message_count: number;
    };
    total_messages: number;
  };
}

export interface DisciplineDistributionQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface DisciplineDistributionItem {
  name: string;
  message_count: number;
}

export interface DisciplineDistributionResponse {
  success: boolean;
  data: {
    disciplines: DisciplineDistributionItem[];
    total_messages: number;
  };
}

export interface IndustryDistributionQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface IndustryDistributionItem {
  name: string;
  message_count: number;
}

export interface IndustryDistributionResponse {
  success: boolean;
  data: {
    industries: IndustryDistributionItem[];
    total_messages: number;
  };
}

export interface QuestionRelevanceQueryParams {
  startDate: string;
  endDate: string;
  signal?: AbortSignal;
}

export interface RelevanceDailyStat {
  date: string;
  avg_relevance_score: number;
  question_count?: number;
}

export interface QuestionRelevanceResponse {
  success: boolean;
  data: {
    daily_stats: RelevanceDailyStat[];
    overall_avg_score: number;
  };
}

export interface QuestionAnalysisAllQueryParams {
  startDate: string;
  endDate: string;
  signal?: AbortSignal;
}

export interface QuestionAnalysisAllResponse {
  success: boolean;
  data: {
    total_messages: number;
    related_messages: number;
    non_related_messages: number;
    web_search_enabled_messages: number;
    sdg_relevance_ratio: number;
    web_search_ratio_in_related?: number;
    web_search_ratio?: number;
  };
}

export interface NonRelevantQuestionsQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface NonRelevantQuestionItem {
  message_id: string;
  question: string;
  question_timestamp: string;
  user_id: number;
  username: string;
}

export interface NonRelevantQuestionsResponse {
  success: boolean;
  data: {
    questions: NonRelevantQuestionItem[];
    total_found: number;
  };
}

export interface WebSearchQuestionsQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface WebSearchQuestionItem {
  message_id: string;
  question: string;
  question_timestamp: string;
  user_id: number;
  username: string;
}

export interface WebSearchQuestionsResponse {
  success: boolean;
  data: {
    questions: WebSearchQuestionItem[];
    total_found: number;
  };
}

export interface QuestionAnalysisCountsQueryParams {
  startDate: string;
  endDate: string;
  signal?: AbortSignal;
}

export interface QuestionAnalysisCountsDailyStat {
  date: string;
  non_related_messages: number;
  web_search_enabled_messages: number;
  total_messages: number;
}

export interface QuestionAnalysisCountsResponse {
  success: boolean;
  data: {
    daily_stats: QuestionAnalysisCountsDailyStat[];
    overall_stats: {
      non_related_messages: number;
      web_search_enabled_messages: number;
      total_messages: number;
      avg_daily_non_related: number;
      avg_daily_web_search: number;
    };
  };
}

export interface MessageSearchQueryParams {
  keyword: string;
  limit?: number;
  startDate?: string;
  endDate?: string;
  signal?: AbortSignal;
}

export interface MessageSearchResultItem {
  message_id: string;
  question: string;
  answer: string;
  question_timestamp: string;
  user_id: number;
  username: string;
}

export interface MessageSearchResponse {
  success: boolean;
  data: {
    keyword: string;
    results: MessageSearchResultItem[];
    total_found: number;
  };
}

export interface ActiveUsersQueryParams {
  startDate: string;
  endDate: string;
  limit?: number;
  signal?: AbortSignal;
}

export interface ActiveUserItem {
  user_id: number;
  username: string;
  message_count: number;
  rank: number;
}

export interface ActiveUsersResponse {
  success: boolean;
  data: {
    users: ActiveUserItem[];
    total_active_users: number;
    date_range: {
      start_date: string;
      end_date: string;
    };
  };
}

export const getTopicsDistribution = async ({
  startDate,
  endDate,
  limit = 5,
  signal,
}: TopicsDistributionQueryParams): Promise<TopicsDistributionResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      limit: String(limit),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/topics/distribution/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching dashboard topics distribution:', error);
    throw error;
  }
};

export const getDisciplineDistribution = async ({
  startDate,
  endDate,
  limit = 10,
  signal,
}: DisciplineDistributionQueryParams): Promise<DisciplineDistributionResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      ...(limit !== undefined && { limit: String(limit) }),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/discipline/distribution/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching dashboard discipline distribution:', error);
    throw error;
  }
};

export const getIndustryDistribution = async ({
  startDate,
  endDate,
  limit = 10,
  signal,
}: IndustryDistributionQueryParams): Promise<IndustryDistributionResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      ...(limit !== undefined && { limit: String(limit) }),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/industry/distribution/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching dashboard industry distribution:', error);
    throw error;
  }
};

export const getQuestionRelevance = async ({
  startDate,
  endDate,
  signal,
}: QuestionRelevanceQueryParams): Promise<QuestionRelevanceResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/questions/relevance/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching dashboard relevance trends:', error);
    throw error;
  }
};

export const getQuestionAnalysisAll = async ({
  startDate,
  endDate,
  signal,
}: QuestionAnalysisAllQueryParams): Promise<QuestionAnalysisAllResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/questions/analysis/all/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching question analysis data:', error);
    throw error;
  }
};

export const getNonRelevantQuestions = async ({
  startDate,
  endDate,
  limit = 20,
  signal,
}: NonRelevantQuestionsQueryParams): Promise<NonRelevantQuestionsResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      ...(limit !== undefined && { limit: String(limit) }),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/questions/non-relevant/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching non-relevant questions:', error);
    throw error;
  }
};

export const getWebSearchQuestions = async ({
  startDate,
  endDate,
  limit = 20,
  signal,
}: WebSearchQuestionsQueryParams): Promise<WebSearchQuestionsResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      ...(limit !== undefined && { limit: String(limit) }),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/questions/web-search-enabled/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching web-search questions:', error);
    throw error;
  }
};

export const getQuestionAnalysisCounts = async ({
  startDate,
  endDate,
  signal,
}: QuestionAnalysisCountsQueryParams): Promise<QuestionAnalysisCountsResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/questions/analysis/counts/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching question analysis counts:', error);
    throw error;
  }
};

export const searchMessages = async ({
  keyword,
  limit,
  startDate,
  endDate,
  signal,
}: MessageSearchQueryParams): Promise<MessageSearchResponse> => {
  try {
    const query = new URLSearchParams({
      keyword,
      ...(limit !== undefined && { limit: String(limit) }),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/messages/search/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw error;
    }
    console.error('Error searching messages:', error);
    throw error;
  }
};

export const getActiveUsers = async ({
  startDate,
  endDate,
  limit = 20,
  signal,
}: ActiveUsersQueryParams): Promise<ActiveUsersResponse> => {
  try {
    const query = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      limit: String(limit),
    });

    const response = await apiFetch(
      `${API_BASE_URL}/api/chat-dashboard/users/active/?${query.toString()}`,
      { method: 'GET', signal },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching dashboard active users:', error);
    throw error;
  }
};
